import { useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";

/// <reference types="google.maps" />
import { useGoogleMapsScript } from "@/hooks/use-google-maps-script";

export interface ParsedPlace {
  location_city: string;
  location_country: string;
  location_formatted_address: string;
  location_lat: string;
  location_lng: string;
  location_name: string;
  location_neighborhood: string;
  location_place_id: string;
  location_postal_code: string;
  location_state: string;
  location_street: string;
}

interface PlacesAutocompleteProps {
  defaultValue?: string;
  onPlaceSelected: (place: ParsedPlace) => void;
}

export function PlacesAutocomplete({
  defaultValue,
  onPlaceSelected,
}: PlacesAutocompleteProps) {
  const { ready } = useGoogleMapsScript();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const elementRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(
    null,
  );
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;
  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  useEffect(() => {
    if (!ready || !containerRef.current || elementRef.current) return;

    const el = new google.maps.places.PlaceAutocompleteElement({
      requestedRegion: "us",
      types: ["establishment", "geocode"],
    });

    el.addEventListener("gmp-select", async (event: any) => {
      const { placePrediction } = event;
      if (!placePrediction) return;

      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: [
          "addressComponents",
          "displayName",
          "formattedAddress",
          "id",
          "location",
        ],
      });

      const comps = place.addressComponents;
      if (!comps) return;

      const streetNumber = getComponent(comps, "street_number");
      const route = getComponent(comps, "route");
      const street = [streetNumber, route].filter(Boolean).join(" ");

      const parsed: ParsedPlace = {
        location_city:
          getComponent(comps, "locality") ||
          getComponent(comps, "sublocality_level_1"),
        location_country: getComponent(comps, "country", true),
        location_formatted_address: place.formattedAddress ?? "",
        location_lat: String(place.location?.lat() ?? ""),
        location_lng: String(place.location?.lng() ?? ""),
        location_name: place.displayName ?? "",
        location_neighborhood:
          getComponent(comps, "neighborhood") ||
          getComponent(comps, "sublocality"),
        location_place_id: place.id ?? "",
        location_postal_code: getComponent(comps, "postal_code"),
        location_state: getComponent(
          comps,
          "administrative_area_level_1",
          true,
        ),
        location_street: street,
      };

      console.log("[PlacesAutocomplete] raw lat/lng from Google:", {
        lat: place.location?.lat(),
        lng: place.location?.lng(),
        parsedLat: parsed.location_lat,
        parsedLng: parsed.location_lng,
      });
      onPlaceSelectedRef.current(parsed);
    });

    const container = containerRef.current;
    container.appendChild(el as unknown as Node);
    elementRef.current = el;

    // Set default value and placeholder by reaching into the input inside the web component
    requestAnimationFrame(() => {
      const input =
        (el as unknown as HTMLElement).querySelector?.("input") ??
        (el as unknown as HTMLElement).shadowRoot?.querySelector("input");
      if (input) {
        if (defaultValueRef.current) {
          input.value = defaultValueRef.current;
        }
        input.placeholder = "event location";
      }
    });

    return () => {
      container.innerHTML = "";
      elementRef.current = null;
    };
  }, [ready]);

  return (
    <View style={styles.field}>
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        ref={containerRef}
        style={{ position: "relative", zIndex: 1000 }}
      />
    </View>
  );
}

function getComponent(
  components: google.maps.places.AddressComponent[],
  type: string,
  useShort = false,
): string {
  const c = components.find((c) => c.types.includes(type));
  return c ? (useShort ? (c.shortText ?? "") : (c.longText ?? "")) : "";
}

const styles = StyleSheet.create({
  field: {
    marginBottom: 16,
  },
});
